<script lang="ts" setup>
const props = defineProps<{
  embedCode: string
}>()

const instanceId = useId()

/*
  We expect the embed code contains <div>, <iframe> and <script> tags.
  The <div> and <iframe> tags can contain any attributes, which will be applied to the elements rendered by this component.
  The <script> tag can contain any attributes, which will be applied to the script tags added to the body.
  Script tags can be external (with src attribute) or inline (with content).

  Tested with:
  - Publitas
  - Fillout
  - Flockler
  - Youtube
  - Flowbox

  Works possibly with:
  - Vimeo
  - TikTok

  ToDo:
  - Style tags

  Exmples:

  --- Publitas ---

  <div id="publitas-embed-ixmwaih1e9"></div>
  < script  data-cfasync="false"  data-height="undefined"  data-publication="https://view.publitas.com/woonboulevard-poortvliet/folder-keukendepot-september/"  data-publication-aspect-ratio="1.4143007360672977"  data-publication-title="Folder | September | Keukendepot"  data-responsive="true"  data-width="undefined"  data-wrapper-id="publitas-embed-ixmwaih1e9"  publitas-embed  src="https://view.publitas.com/embed.js"  type="text/javascript">< /script>

  --- Flowbox ---

  < script>
    (function(d, id) {
      if (!window.flowbox) { var f = function () { f.q.push(arguments); }; f.q = []; window.flowbox = f; }
      if (d.getElementById(id)) {return;}
      var s = d.createElement('script'), fjs = d.scripts[d.scripts.length - 1]; s.id = id; s.async = true;
      s.src = 'https://connect.getflowbox.com/flowbox.js';
      fjs.parentNode.insertBefore(s, fjs);
    })(document, 'flowbox-js-embed');
  < /script>

  <div id="js-flowbox-flow"></div>

  < script>
    window.flowbox('init', {
      container: '#js-flowbox-flow',
      key: 'xLP-d-iJRSeePxZIYA4GKQ',
      locale: 'en-GB',
    })
  < /script>

  --- Fillout ---

  <div style="width:100%;height:500px;" data-fillout-id="ndQbqWeu1Jus" data-fillout-embed-type="standard" data-fillout-inherit-parameters data-fillout-dynamic-resize></div>
  < script src="https://server.fillout.com/embed/v1/">< /script>

*/

const iframeDataList = computed(() => {
  const iframeMatches = Array.from(props.embedCode.matchAll(/<iframe([^>]*)\/?>(?:<\/iframe>)?/g))
  return iframeMatches.map((match, index) => {
    const attributes = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    return { ...attributes, key: `iframe__${instanceId}__${index}` }
  })
})

const divDataList = computed(() => {
  const divMatches = Array.from(props.embedCode.matchAll(/<div([^>]*)>(?:.*?<\/div>)?/gs))
  return divMatches.map((match, index) => {
    const attributes = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    return { ...attributes, key: `div__${instanceId}__${index}` }
  })
})

const scriptDataList = computed(() => {
  const scriptMatches = Array.from(props.embedCode.matchAll(/<script([^>]*)>(.*?)<\/script>/gs))

  return scriptMatches.map((match, _index) => {
    const attributes = Object.fromEntries(
      Array.from(match[1].matchAll(/([\w-:]+)(?:="([^"]*)")?/g))
        .map(([_, key, value]) => [key, value || true]),
    )
    const scriptContent = match[2]?.trim()
    return {
      ...attributes,
      content: scriptContent || undefined,
      origin: match[0],
    }
  })
})

const scriptConfigs = computed(() => {
  return scriptDataList.value.map((scriptData, index) => {
    const id = `script__${instanceId}__${index}`
    const key = scriptData.origin || id

    return {
      key,
      id: scriptData.id || id,
      defer: scriptData.src ? true : undefined,
      ...scriptData,
    }
  })
})

const { initiatedThirdParty } = useApp()

function initScripts() {
  if (!initiatedThirdParty.value) {
    return
  }

  scriptConfigs.value.forEach((scriptConfig) => {
    if (!document.getElementById(scriptConfig.id)) {
      const script = document.createElement('script')
      Object.entries(scriptConfig).forEach(([key, value]) => {
        if (key === 'key' || key === 'content') {
          return
        }

        if (value === true) {
          script.setAttribute(key, '')
        }
        else if (value !== undefined) {
          script.setAttribute(key, value as string)
        }
      })

      if (scriptConfig.content) {
        script.textContent = scriptConfig.content
      }

      document.body.appendChild(script)
    }
  })
}

watch(initiatedThirdParty, () => {
  initScripts()
})

onMounted(() => {
  initScripts()
})

onUnmounted(() => {
  scriptConfigs.value.forEach((scriptConfig) => {
    const script = document.getElementById(scriptConfig.id)
    if (script) {
      script.remove()
    }
  })
})
</script>

<template>
  <template v-if="initiatedThirdParty">
    <div
      v-for="divData in divDataList"
      :key="divData.key"
      v-bind="divData"
    />
    <iframe
      v-for="iframeData in iframeDataList"
      :key="iframeData.key"
      v-bind="iframeData"
    />
  </template>
</template>

<style>
@import url('~layers/app/components/Ui/Embed.css');
</style>
